import React, { useState } from "react";
import { useAuth } from "../../auth/useAuth";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { OpeningHours, StoreData } from "./stores.types";
import axios from "axios";
import { API_URL } from "../../config/config";
import { useNavigate } from "react-router-dom";

const CreateStore: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState<StoreData>({
    revenue: 0,
    Inventories: [],
    id: "",
    id_BusinessLocation: "",
    name: "",
    address: "",
    phone_number: "",
    email: "",
    opening_hours: {
      "mon-fri": "",
      sat: "",
      sun: "",
    },
    status: "active",
    capacity: 0,
    latitude: 0,
    longitude: 0,
    region: "",
    city: "",
    store_code: "",
    image_url: "",
    isConnectedToIg: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  if (!user) {
    return null;
  }
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name.startsWith("opening_hours.")) {
      const key = name.split(".")[1] as keyof OpeningHours;
      setFormData((prev) => ({
        ...prev,
        opening_hours: {
          ...prev.opening_hours,
          [key]: value,
        } as OpeningHours,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    const url = `${API_URL}/store/${user.id}/`;

    try {
      const formToSend = new FormData();

      formToSend.append("name", formData.name);
      formToSend.append("address", formData.address);
      formToSend.append("phone_number", formData.phone_number);
      formToSend.append("email", formData.email);
      formToSend.append("capacity", formData.capacity?.toString() ?? "");
      formToSend.append("latitude", formData.latitude?.toString() ?? "");
      formToSend.append("longitude", formData.longitude?.toString() ?? "");
      formToSend.append("region", formData.region ?? "");
      formToSend.append("city", formData.city ?? "");
      formToSend.append("store_code", formData.store_code ?? "");
      formToSend.append("status", formData.status);

      // Handle opening_hours (must be stringified)
      const openingHours = {
        "mon-fri": formData.opening_hours?.["mon-fri"] ?? "",
        sat: formData.opening_hours?.sat ?? "",
        sun: formData.opening_hours?.sun ?? "",
      };
      formToSend.append("opening_hours", JSON.stringify(openingHours));

      // ✅ Append image if selected
      if (selectedFile) {
        formToSend.append("store_image", selectedFile);
      }

      await axios.post(url, formToSend); // remove headers

      setSuccess(true);
      setSelectedFile(null);
      navigate("/stores");
      // Optionally reset formData here
    } catch (err: any) {
      console.error("Error creating store:", err);
      setError(err.response?.data?.error || err.message || "Unexpected error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        maxWidth: 600,
        mx: "auto",
        p: 4,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        boxShadow: 3,
        borderRadius: 2,
        bgcolor: "#fff",
      }}
    >
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Create Store
      </Typography>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">Store created successfully!</Alert>}

      <TextField
        label="Store Name"
        name="name"
        value={formData.name}
        onChange={handleChange}
        required
        fullWidth
      />
      <TextField
        label="Address"
        name="address"
        value={formData.address}
        onChange={handleChange}
        required
        fullWidth
      />
      <TextField
        label="Phone Number"
        name="phone_number"
        value={formData.phone_number}
        onChange={handleChange}
        required
        fullWidth
      />
      <TextField
        label="Email"
        name="email"
        type="email"
        value={formData.email}
        onChange={handleChange}
        required
        fullWidth
      />

      <Typography variant="subtitle1" sx={{ mt: 2 }}>
        Opening Hours
      </Typography>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flex: "1 1 200px" }}>
          <TextField
            label="Mon-Fri"
            name="opening_hours.mon-fri"
            value={formData.opening_hours?.["mon-fri"] ?? ""}
            onChange={handleChange}
            fullWidth
            placeholder="Example: 00:00-00:00"
          />
        </Box>
        <Box sx={{ flex: "1 1 200px" }}>
          <TextField
            label="Saturday"
            name="opening_hours.sat"
            value={formData.opening_hours?.sat ?? ""}
            onChange={handleChange}
            fullWidth
            placeholder="Example: 00:00-00:00"
          />
        </Box>
        <Box sx={{ flex: "1 1 200px" }}>
          <TextField
            label="Sunday"
            name="opening_hours.sun"
            value={formData.opening_hours?.sun ?? ""}
            onChange={handleChange}
            fullWidth
            placeholder="Example: 00:00-00:00"
          />
        </Box>
      </Box>

      <FormControl fullWidth>
        <InputLabel id="status-label">Status</InputLabel>
        <Select
          labelId="status-label"
          name="status"
          value={formData.status}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, status: e.target.value }))
          }
          label="Status"
        >
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="closed">Closed</MenuItem>
          <MenuItem value="under_maintenance">Under Maintenance</MenuItem>
        </Select>
      </FormControl>

      <TextField
        label="Capacity"
        name="capacity"
        type="number"
        value={formData.capacity}
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Latitude"
        name="latitude"
        type="number"
        value={formData.latitude}
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Longitude"
        name="longitude"
        type="number"
        value={formData.longitude}
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Region"
        name="region"
        value={formData.region}
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="City"
        name="city"
        value={formData.city}
        onChange={handleChange}
        fullWidth
      />
      <TextField
        label="Store Code"
        name="store_code"
        value={formData.store_code}
        onChange={handleChange}
        fullWidth
      />
      <Button variant="contained" component="label" sx={{ mt: 2 }}>
        Upload New Image
        <input
          type="file"
          hidden
          onChange={handleImageChange}
          accept="image/*"
        />
      </Button>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : undefined
          }
        >
          {loading ? "Submitting..." : "Create Store"}
        </Button>
      </Box>
    </Box>
  );
};
export default CreateStore;
